/**
 * Utility functions for color manipulation
 * Used to create dynamic color schemes based on primary color from config
 */

function hexToRgb(hex) {
  const match = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!match) return null;
  
  return {
    red: parseInt(match[1], 16),
    green: parseInt(match[2], 16),
    blue: parseInt(match[3], 16)
  };
}

function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
}

/**
 * Mix two hex colors by a given amount (0-1)
 * @param {string} baseHex - Base color in hex format
 * @param {string} targetHex - Target color in hex format
 * @param {number} amount - Mix ratio (0 = baseHex, 1 = targetHex)
 * @returns {string} - Mixed color in hex format
 */
function mixHexColors(baseHex, targetHex, amount) {
  const base = hexToRgb(baseHex);
  const target = hexToRgb(targetHex);

  if (!base || !target) {
    return baseHex || targetHex;
  }

  const mix = (start, end) => Math.round(start + (end - start) * amount);

  return rgbToHex(
    mix(base.red, target.red),
    mix(base.green, target.green),
    mix(base.blue, target.blue)
  );
}

/**
 * Build a tint palette from a base color
 * @param {string} baseHex - Base color in hex format
 * @param {number} count - Number of tints to generate
 * @returns {array} - Array of hex colors
 */
function buildTintPalette(baseHex, count) {
  const palette = [];

  for (let index = 0; index < count; index += 1) {
    const ratio = count <= 1 ? 0 : index / (count - 1);
    const target = ratio < 0.7 ? "#ffffff" : "#111827";
    const amount = ratio < 0.7 ? ratio / 0.7 * 0.42 : (ratio - 0.7) / 0.3 * 0.22;

    palette.push(mixHexColors(baseHex, target, amount));
  }

  return palette;
}

/**
 * Convert hex color to CSS variable or inline style
 * @param {string} hex - Color in hex format
 * @returns {object} - Style object with rgb values
 */
function hexToRgbStyle(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return {};
  return {
    '--color-r': rgb.red,
    '--color-g': rgb.green,
    '--color-b': rgb.blue
  };
}

/**
 * Get button color classes based on primary color
 * Returns inline styles for dynamic button coloring
 * @param {string} primaryColor - Primary color in hex format
 * @param {string} state - 'normal', 'hover', 'active'
 * @returns {object} - Style object for the button
 */
function getButtonColorStyle(primaryColor, state = 'normal') {
  if (!primaryColor) primaryColor = '#2f4f4f';

  const colors = {
    normal: primaryColor,
    hover: mixHexColors(primaryColor, '#111827', 0.15), // Darker
    active: mixHexColors(primaryColor, '#111827', 0.25)  // Even darker
  };

  return {
    backgroundColor: colors[state] || colors.normal,
    color: '#ffffff'
  };
}

/**
 * Get dynamic background color that complements the primary color
 * @param {string} primaryColor - Primary color in hex format
 * @param {number} opacity - Opacity level (0-1)
 * @returns {string} - RGBA color string
 */
function getLighterBackground(primaryColor, opacity = 0.1) {
  if (!primaryColor) primaryColor = '#2f4f4f';
  
  const rgb = hexToRgb(primaryColor);
  if (!rgb) return `rgba(47, 79, 79, ${opacity})`;
  
  return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${opacity})`;
}

export {
  hexToRgb,
  rgbToHex,
  mixHexColors,
  buildTintPalette,
  hexToRgbStyle,
  getButtonColorStyle,
  getLighterBackground
};
